import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../../assets/Icons";
import SmallPopupWithInput from "../../../../../components/_utils/CommonSmallPopupWithInput";
import StatusContainer from "../../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import CommonToast from "../../../../../components/_utils/CommonToast";
import UniversalRefreshToast from "../../../../../components/_utils/UniversalRefreshToast";
import {
  approveBeneficiaryAccount,
  beneficiaryAccountDetailsById,
  rejectBeneficiaryAccount,
} from "../../../../../redux/features/accounts/beneficiaryAccount/index.js";
import { setHideBreadCrumb } from "../../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import { GlobalConfiguration } from "../../../../../utils/configurationProvider";
import globalText from "../../../../../utils/globalText";
import BeneAccountDeclinePopup from "../../../../../components/Accounts/BeneficiaryAccount/BeneAccountDeclinePopup/index.js";

const BeneficiaryAccountEditSuccess = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { id, successType } = useParams();
  const updatedFields = location?.state?.updatedFields || [];
  const [showLoader, setShowLoader] = useState(false);
  const [rejectPopup, setRejectPopup] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [currentAccountDetails, setCurrentAccountDetails] = useState({
    id: "",
    name: "",
    bank_name: "",
    bank_acc_no: "",
    ifsc_code: "",
    categoryIds: [],
    pan: "",
    labels: "",
  });

  const services = useSelector((state) => state.refreshToken.services);
  const { beneficiaryAccountDetailsData } = useSelector(
    (state) => state.beneficiaryAccount,
  );

  const beneAccountSuccessData =
    beneficiaryAccountDetailsData?.beneficiary_accounts[0];

  const canApprove =
    beneAccountSuccessData?.approval_status === "pending" &&
    services?.includes("BAAccount");

  const canCreateTxn = services?.includes("CBeneficiaryTxn");

  const handleInputChange = (event) => {
    let value = event?.target?.value;
    value = value.replace(/[^a-zA-Z0-9\s]/g, "");
    value = value.replace(/^\s+/, "");
    value = value.replace(/\s{2,}/g, " ");
    value = value.slice(0, 150);
    setDeclineReason(value);
  };

  const computeVariant = (value, focused) => {
    const length = (value ?? "").trim().length;

    if (length === 0) return "default";

    if (length >= GlobalConfiguration.rejectReasonMinLength) {
      return "verified";
    }

    return focused ? "typing" : "error";
  };

  const getCategoryNames = (acc) => {
    const names = (acc?.category_ids || [])
      .map((c) => c?.category_name)
      .filter(Boolean);
    return names.length ? names.join(", ") : "Not assigned";
  };

  const contactFieldConfig = {
    account_name: {
      leftSubTitle:
        location?.state?.selectedAccountNameOption === "legalname"
          ? "Account name - Using legal name"
          : "Account name",
      getTitle: (acc) => (acc?.name ? acc.name : "Not available"),
    },
    contact_no: {
      leftSubTitle: "Contact number",
      icon: ICONS?.CONTACT_WITH_WHITE_CIRCLE,
      iconSize: "small",
      getTitle: (acc) =>
        acc?.contact_no ? `+91 ${acc.contact_no}` : "Not available",
      bgNeeded: false,
    },
    email_id: {
      leftSubTitle: "Email ID",
      icon: ICONS?.CONTACT_US_MAIL,
      iconSize: "small",
      getTitle: (acc) => acc?.email_id || "Not available",
      bgNeeded: false,
    },
  };

  const categoryFieldConfig = {
    category: {
      leftSubTitle: "Assigned categories",
      getTitle: (acc) => getCategoryNames(acc) || "Not assigned",
    },
  };

  const getFieldsToShow = () => {
    if (updatedFields.length > GlobalConfiguration.lengthCheckZero) {
      return updatedFields;
    }
    if (successType === "contact_edit_success") {
      return ["account_name", "contact_no", "email_id"];
    }
    if (successType === "category_edit_success") {
      return ["category"];
    }
    return null;
  };

  const fieldsToShow = getFieldsToShow();
  const showOnlyUpdated =
    fieldsToShow &&
    (successType === "contact_edit_success" ||
      successType === "category_edit_success");

  const getSuccessTitle = () => {
    if (successType === "category_edit_success") {
      return "Beneficiary categories updated successfully.";
    }
    if (successType === "contact_edit_success") {
      const hasContactUpdate = updatedFields.some((f) =>
        ["contact_no", "email_id"].includes(f),
      );
      const hasNameUpdate = updatedFields.includes("account_name");
      if (hasNameUpdate && !hasContactUpdate)
        return "Account details updated successfully.";
      if (hasContactUpdate && !hasNameUpdate)
        return "Account details updated successfully.";
      if (hasNameUpdate && hasContactUpdate)
        return "Account details updated successfully.";
    }
    return "Details updated successfully.";
  };

  const renderInfoCards = () => {
    if (successType === "contact_edit_success") {
      return fieldsToShow
        .filter((key) => contactFieldConfig[key])
        .map((key) => {
          const config = contactFieldConfig[key];
          return (
            <InfoCard
              key={key}
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: config.getTitle(beneAccountSuccessData),
                  leftSubTitle: config.leftSubTitle,
                  ...(config.icon && {
                    icon: config.icon,
                  }),
                  ...(config.iconSize && { iconSize: config.iconSize }),
                  ...(config.bgNeeded === false && { bgNeeded: false }),
                },
              ]}
            />
          );
        });
    }
    if (successType === "category_edit_success") {
      return fieldsToShow
        .filter((key) => categoryFieldConfig[key])
        .map((key) => {
          const config = categoryFieldConfig[key];
          return (
            <InfoCard
              key={key}
              bgColor="var(--positive-card)"
              rows={[
                {
                  leftTitle: config.getTitle(beneAccountSuccessData),
                  leftSubTitle: config.leftSubTitle,
                },
              ]}
            />
          );
        });
    }
    return null;
  };

  const handlers = {
    createAccount: () =>
      navigate("/beneficiary_account_dashboard/create_beneficiary_account", {
        replace: true,
      }),
    createTxn: () => {
      if (!services?.includes("CBeneficiaryTxn")) {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "CBeneficiaryTxn" },
        });
        return;
      }

      navigate(
        "/beneficiary_transaction_dashboard/create_beneficiary_transaction",
        {
          state: currentAccountDetails,
          replace: true,
        },
      );
    },
    viewDetails: () =>
      navigate(
        `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
        {
          replace: true,
        },
      ),
    dashboard: () =>
      navigate("/beneficiary_account_dashboard", {
        replace: true,
      }),
  };

  const accountApproval = () => {
    (async () => {
      setShowLoader(true);
      toast.dismiss();
      UniversalRefreshToast("approvalaccountsRefresh");
      const response = await dispatch(approveBeneficiaryAccount({ id }));
      if (approveBeneficiaryAccount.fulfilled.match(response)) {
        toast.dismiss();

        CommonToast({
          text: "Beneficiary account approved.",
          variant: "var(--success)",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        });

        navigate(
          `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
          { replace: true },
        );
      } else {
        setShowLoader(false);
        toast.dismiss();
        CommonToast({
          text: response?.payload?.errors?.beneficiary_account?.[0],
          variant: "var(--error)",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        });
      }
    })();
  };

  const accountRejected = () => {
    (async () => {
      setShowLoader(true);
      toast.dismiss();
      UniversalRefreshToast("accountsRefresh");

      const response = await dispatch(
        rejectBeneficiaryAccount({
          id,
          rejection_reason: declineReason,
        }),
      );

      if (rejectBeneficiaryAccount.fulfilled.match(response)) {
        toast.dismiss();

        CommonToast({
          text: "Beneficiary account rejected.",
          variant: "var(--success)",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        });

        navigate(
          `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
          { replace: true },
        );
      } else {
        setShowLoader(false);
        toast.dismiss();
        CommonToast({
          text: response?.payload?.errors?.beneficiary_account?.[0],
          variant: "var(--error)",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        });

        setRejectPopup(false);
      }
    })();
  };

  const primaryBtn = {
    enabledBgColor: "var(--text-primary)",
    enabledTextColor: "var(--positive-bg)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    imgColor: "var(--positive-bg)",
    hoverOption: "dark",
  };

  const outlineBtn = {
    enabledBgColor: "var(--bg-positive-bg)",
    enabledTextColor: "var(--text-primary)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    outLineColor: "var(--text-primary)",
    hoverOption: "dark",
  };

  const buttonRows = [
    ...(canApprove
      ? [
          [
            {
              btnText: globalText?.REJECT_ACCOUNT,
              enabledBgColor: "var(--negative-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS?.REJECT_ICON_RED_WITH_WHITE_CIRCLE,
              handleFunction: () => {
                if (!showLoader) {
                  setRejectPopup(true);
                }
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
              isDisabled: showLoader,
            },
            {
              btnText: globalText?.APPROVE_ACCOUNT,
              enabledBgColor: "var(--positive-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
              handleFunction: () => {
                if (!showLoader) {
                  accountApproval();
                }
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
              isDisabled: showLoader,
            },
          ],
        ]
      : []),

    [
      {
        ...primaryBtn,
        btnText: globalText?.CREATE_ACCOUNT,
        icon: ICONS?.CREATE_NEW_ACCOUNT,
        handleFunction: handlers.createAccount,
        isDisabled: showLoader,
      },
      {
        ...primaryBtn,
        btnText: globalText?.CREATE_TRANSACTION,
        icon: ICONS?.ADD_BUTTON_WHITE,
        handleFunction: handlers.createTxn,
        isDisabled: !canCreateTxn || showLoader,
      },
      {
        ...primaryBtn,
        btnText: globalText?.VIEW_DETAILS,
        icon: ICONS?.VIEW_DETAILS,
        handleFunction: handlers.viewDetails,
        isDisabled: showLoader,
      },
      {
        ...outlineBtn,
        btnText: globalText?.GO_TO_DASHBOARD,
        icon: ICONS?.GO_TO_DASHBOARD,
        handleFunction: handlers.dashboard,
        imgColor: "var(--text-primary)",
        isDisabled: showLoader,
      },
    ],
  ];

  useEffect(() => {
    dispatch(beneficiaryAccountDetailsById({ id }));
    dispatch(setHideBreadCrumb({ hide: true }));

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [id, dispatch]);
  const updatedCategoryIds = location?.state?.updatedCategoryIds;
  useEffect(() => {
    if (beneAccountSuccessData) {
      const {
        id,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        category_ids,
        pan_no,
        labels,
      } = beneAccountSuccessData;

      const categoryIds = updatedCategoryIds?.length
        ? updatedCategoryIds
        : (category_ids || []).map((c) => c.category_id ?? c.id);
      setCurrentAccountDetails({
        id,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        categoryIds,
        pan: pan_no,
        labels: labels,
      });
    }
  }, [beneAccountSuccessData, updatedCategoryIds]);

  return (
    <>
      {rejectPopup && (
             <BeneAccountDeclinePopup
          headerTitle={globalText?.REJECT_REASON_LABEL}
          bulletPoints={[
            globalText.REJECT_POPUP_BULLET_POINT_ONE,
            globalText.REJECT_POPUP_BULLET_POINT_TWO,
            globalText.REJECT_POPUP_BULLET_POINT_THREE,
          ]}
          declineReason={declineReason}
          setDeclineReason={setDeclineReason}
          showLoader={showLoader}
          setShowLoader={setShowLoader}
          handleClosePopup={() => {
            setRejectPopup(false);
            setDeclineReason("");
          }}
          handleFunctionClick={() => {
            accountRejected();
            setDeclineReason("");
          }}
        />
      )}
      <div>
        <StatusContainer
          status="success"
          buttonRows={buttonRows}
          title={getSuccessTitle()}
        >
          <TabsWeapperSection title="Updated details">
            <>
              {showOnlyUpdated ? (
                <>
                  {renderInfoCards()}

                  <InfoCard
                    bgColor="var(--positive-card)"
                    rows={[
                      {
                        leftTitle: beneAccountSuccessData?.id,
                        leftSubTitle: "Account ID",
                        copyText: beneAccountSuccessData?.id,
                      },
                    ]}
                  />
                </>
              ) : (
                <>
                  <InfoCard
                    bgColor="var(--positive-card)"
                    rows={[
                      {
                        leftTitle: getCategoryNames(beneAccountSuccessData),
                        leftSubTitle: "Assigned categories",
                      },
                    ]}
                  />
                  <InfoCard
                    bgColor="var(--positive-card)"
                    rows={[
                      {
                        leftTitle: beneAccountSuccessData?.name,
                        leftSubTitle: "Account name",
                      },
                    ]}
                  />
                  <InfoCard
                    bgColor="var(--positive-card)"
                    rows={[
                      {
                        leftTitle: beneAccountSuccessData?.contact_no,
                        leftSubTitle: "Contact number",
                        icon: ICONS?.CONTACT_WITH_WHITE_CIRCLE,
                        iconSize: "small",
                        bgNeeded: false,
                      },
                    ]}
                  />
                  <InfoCard
                    bgColor="var(--positive-card)"
                    rows={[
                      {
                        leftTitle: beneAccountSuccessData?.email_id,
                        leftSubTitle: "Email ID",
                        icon: ICONS?.CONTACT_US_MAIL,
                        iconSize: "small",
                        bgNeeded: false,
                      },
                    ]}
                  />
                </>
              )}
            </>
          </TabsWeapperSection>
        </StatusContainer>
      </div>
    </>
  );
};

export default BeneficiaryAccountEditSuccess;
